import React, { FC, memo, useEffect, useMemo, useState } from 'react'

import UploadFirmwarePanel from '../../support/features/upload-firmware-panel/uploadFirmwarePanel'
import {
    FirmwareGeneralModel,
    useFirmwareDeleteFirmwareMutation,
    useFirmwareGetActiveFirmwareQuery,
    useFirmwareGetFirmwaresByFilterQuery,
    useFirmwareUploadFirmwareMutation,
} from '../../common/redux/api/web-api'
import { FirmwareType, Product } from '../../../support/firmware/firmwarePage'
import useText from '../../common/hooks/useText'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import ConfirmationModal from '../ConfirmationModal'

import FirmwareList from './firmwareList'

export type UploadFirmwareErrors = {
    firmwareVersion?: string
    description?: string
    file?: string
}

export interface IFirmware {
    id: number
    productId: number
    firmwareVersion: number
    firmwareType: FirmwareType
    description: string
    uploadDate: string
    ocppEnabledFirmware: boolean
    firmwareStatus: string
    isActive: boolean
    chargePointsCount: number
}

const defaultUploadFirmwareErrorsValue: UploadFirmwareErrors = {
    firmwareVersion: undefined,
    description: undefined,
    file: undefined,
}

export type UploadFirmwareTabProps = {
    product: Partial<Product>
}
export const DEFAULT_VISIBLE_ITEMS = 3
const maxItemsCount = 1000

const UploadFirmwareTab: FC<UploadFirmwareTabProps> = ({ product }) => {
    const text = useText()
    const [firmwareUploadErrors, setFirmwareUploadErrors] = useState<UploadFirmwareErrors>(
        defaultUploadFirmwareErrorsValue
    )

    const [visibleItemsCount, setVisibleItemsCount] = useState(DEFAULT_VISIBLE_ITEMS)

    const [uploadFirmware] = useFirmwareUploadFirmwareMutation()
    const [deleteFirmware] = useFirmwareDeleteFirmwareMutation()
    const { data: firmware } = useFirmwareGetFirmwaresByFilterQuery(
        {
            productId: product.productId,
            ProductId: product.productId,
            firmwareType: product.firmwareType,
            noOfItems: visibleItemsCount,
            ocppEnabled: product.ocppEnabledFirmware,
        },
        { skip: !product.productId }
    )

    const [availableFirmwares, setAvailableFirmwares] = useState<FirmwareGeneralModel[]>([])
    const [confirmRemoveFirmware, setConfirmRemoveFirmware] = useState(false)
    const [selectedFirmwareId, setSelectedFirmwareId] = useState<number | null>(null)

    const { data: activeFirmwareData, isLoading: activeFirmwareIsLoading } = useFirmwareGetActiveFirmwareQuery(
        {
            productId: product.productId,
        },
        { skip: !product || !product.productId }
    )

    useEffect(() => setAvailableFirmwares(firmware?.items || []), [firmware])

    const totalAvailableFirmwares = useMemo(() => firmware?.totalCount || 0, [firmware?.totalCount])

    const firmwareList = useMemo(() => {
        return activeFirmwareData &&
            activeFirmwareData.firmwareType === product.firmwareType &&
            !product.ocppEnabledFirmware
            ? [
                  {
                      ...activeFirmwareData,
                      isActive: true,
                  },
                  ...availableFirmwares,
              ].filter((obj, index, arr) => {
                  return index === arr.findIndex((o) => obj.id === o.id)
              })
            : availableFirmwares
    }, [availableFirmwares, activeFirmwareData, product])

    useEffect(() => {
        if (product.firmwareVersion) {
            if (
                !availableFirmwares.find(
                    (x) => x.firmwareVersion && Number(x.firmwareVersion) === Number(product.firmwareVersion)
                )
            ) {
                setFirmwareUploadErrors((prevValue) => ({
                    ...prevValue,
                    firmwareVersion: undefined,
                }))
            }
        }
    }, [availableFirmwares, product.firmwareVersion])

    const submitUploadFirmware = (formValues: Product) => {
        const body = new FormData()
        body.append('FirmwareType', product.firmwareType)
        body.append('OcppEnabledFirmware', product.ocppEnabledFirmware)
        body.append('ProductId', product.productId)
        body.append('Title', product.title)
        body.append('FirmwareServerLocationId', product.firmwareServerLocationId)
        body.append('Description', formValues.description)
        body.append('FirmwareVersion', formValues.firmwareVersion)
        body.append('File', formValues.file)

        uploadFirmware({
            productId: product.productId,
            body,
        })
            .unwrap()
            .then(() => {
                notifySuccess(text('msgNewFirmwareUploadedSuccessfully'))
            })
            .catch(() => {
                notifyError(text('msgNewFirmwareUploadedFailed'))
            })
    }

    useEffect(() => {
        setFirmwareUploadErrors(defaultUploadFirmwareErrorsValue)
    }, [product])

    const onRemoveFirmware = (id: number | null, removeIsConfirmed: boolean) => {
        setSelectedFirmwareId(id)
        setConfirmRemoveFirmware(removeIsConfirmed)
    }

    const handleRemoveConfirm = () => {
        if (product.productId && selectedFirmwareId) {
            deleteFirmware({ productId: product.productId, firmwareId: selectedFirmwareId })
                .unwrap()
                .then(
                    () => {
                        onRemoveFirmware(null, false)
                        notifySuccess(text('msgFirmwareRemoved'))
                    },
                    ({ error }) => error && notifyError(error)
                )
        }
    }

    const shouldShowLess = availableFirmwares.length > DEFAULT_VISIBLE_ITEMS
    const shouldShowMore = !shouldShowLess && totalAvailableFirmwares > DEFAULT_VISIBLE_ITEMS

    return (
        <div className="flex">
            <div className="w-12 sm:w-5 md:w-4">
                <UploadFirmwarePanel
                    productName={product.title}
                    firmware={product}
                    errors={firmwareUploadErrors}
                    onSubmit={submitUploadFirmware}
                />
            </div>
            <div className="w-12 sm:w-7 md:w-8 ml-8">
                <FirmwareList
                    showMoreIsVisible={shouldShowMore}
                    onShowMore={() => setVisibleItemsCount(maxItemsCount)}
                    showLessIsVisible={shouldShowLess}
                    onShowLess={() => setAvailableFirmwares(availableFirmwares.slice(0, DEFAULT_VISIBLE_ITEMS))}
                    onRemove={(id) => onRemoveFirmware(id, true)}
                    title={text('msgFWVersions')}
                    data={firmwareList}
                    isOcpp={product.ocppEnabledFirmware ?? false}
                    loading={activeFirmwareIsLoading}
                    showActionMenu={true}
                />
            </div>
            {confirmRemoveFirmware && (
                <ConfirmationModal
                    message={text('msgConfirmFwRemoval')}
                    onClose={() => onRemoveFirmware(null, false)}
                    confirmButtonText={text('lblConfirm')}
                    onConfirm={handleRemoveConfirm}
                    title={text('lblConfirmFirmwareRemove')}
                />
            )}
        </div>
    )
}

export default memo(UploadFirmwareTab)
